import _ from 'lodash';
import { UtilFeatureFlagService, UtilFeatureFlagQnbServiceUnauth } from 'gw-capability-util';
import { FEATURE_FLAG_DEFAULT_VALUES, FEATURE_FLAG_NAMES } from '../StringConstants';
import WMICApplicationUtil from '../WMICApplicationUtil';

const FEATURE_FLAGS_TIMEOUT_MS = 5000;

export default class WMICSplitIoFeatureFlags {
    constructor(authData) {
        this.authData = authData;
        this.init();
    }

    static getWMICSplitIoFeatureFlags(authData) {
        const nextAuthHeader = _.get(authData, 'Authorization') || _.get(authData, 'authHeader.Authorization');
        const currentAuthHeader = this.singleton?.getAdditionalHeaders?.().Authorization;
        const shouldRefreshSingleton = Boolean(nextAuthHeader && nextAuthHeader !== currentAuthHeader);

        if (!this.singleton || shouldRefreshSingleton) {
            this.singleton = new WMICSplitIoFeatureFlags(authData);
        }
        return this.singleton;
    }

    init() {
        this.featureFlags = this.getFlags();
    };  

    wmicUseFlags() {
        const result = this.featureFlags.then((resultFlags) => {return resultFlags});
        return result;
    };

    getFlags() {
        const featureFlagService = this.getFeatureFlagService();
        if (!featureFlagService) {
            return this.getDefaultFlags();
        }

        if (WMICApplicationUtil.isCE()) {
            // Quote-and-buy uses unauthenticated path — no auth header required
            return Promise.resolve()
                .then(() => this.fetchAllFeatureFlagsWithTimeout(featureFlagService, {}))
                .then((response) => this.mapResponseToFlags(response))
                .catch(() => {
                    return this.getDefaultFlags();
                });
        }

        const additionalHeaders = this.getAdditionalHeaders();
        if (!additionalHeaders.Authorization) {
            return this.getDefaultFlags();
        }

        return Promise.resolve()
            .then(() => this.fetchAllFeatureFlagsWithTimeout(featureFlagService, additionalHeaders))
            .then((response) => this.mapResponseToFlags(response))
            .catch(() => {
                return this.getDefaultFlags();
            });
    };

    fetchAllFeatureFlagsWithTimeout(featureFlagService, additionalHeaders) {
        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                reject(new Error('[FeatureFlags] Timeout while fetching flags.'));
            }, FEATURE_FLAGS_TIMEOUT_MS);

            Promise.resolve()
                .then(() => featureFlagService.getAllFeatureFlags(additionalHeaders))
                .then((response) => {
                    clearTimeout(timeoutId);
                    resolve(response);
                })
                .catch((error) => {
                    clearTimeout(timeoutId);
                    reject(error);
                });
        });
    }

    getFeatureFlagService() {
        // AMP uses authenticated UtilFeatureFlagService
        if (WMICApplicationUtil.isAMP() && UtilFeatureFlagService && typeof UtilFeatureFlagService.getAllFeatureFlags === 'function') {
            return UtilFeatureFlagService;
        }
        // Quote-and-buy (CE) uses dedicated unauthenticated QNB feature-flag service
        if (WMICApplicationUtil.isCE() && UtilFeatureFlagQnbServiceUnauth && typeof UtilFeatureFlagQnbServiceUnauth.getAllFeatureFlags === 'function') {
            return UtilFeatureFlagQnbServiceUnauth;
        }
        return undefined;
    }

    getAdditionalHeaders() {
        if (!this.authData) {
            return {};
        }
        if (this.authData.Authorization) {
            return { Authorization: this.authData.Authorization };
        }
        if (_.get(this.authData, 'authHeader.Authorization')) {
            return { Authorization: this.authData.authHeader.Authorization };
        }
        return {};
    }

    mapResponseToFlags(response) {
        const responseFlags = _.get(response, 'Flags')
            || _.get(response, 'flags')
            || _.get(response, 'result.Flags')
            || _.get(response, 'result.flags')
            || [];

        if (!Array.isArray(responseFlags)) {
            throw new Error('[FeatureFlags] Invalid response: expected Flags array.');
        }

        const normalizedFlags = {};

        responseFlags.forEach((flagDto) => {
            const flagName = this.getField(flagDto, ['FlagName', 'flagName']);
            if (!flagName) {
                return;
            }
            normalizedFlags[flagName] = this.normalizeFlag(flagDto);
        });

        Object.keys(FEATURE_FLAG_NAMES).forEach((fKey) => {
            const fName = FEATURE_FLAG_NAMES[fKey];
            if (normalizedFlags[fName] === undefined || normalizedFlags[fName] === null) {
                normalizedFlags[fName] = this.getDefaultFlagByKey(fKey);
            }
        });

        return normalizedFlags;
    }

    normalizeFlag(flagDto) {
        const rawIsAvailable = this.getField(flagDto, ['IsAvailable', 'isAvailable']);
        const normalizedFlag = {
            isAvailable: this.parseBoolean(rawIsAvailable)
        };

        const outageStartDate = this.getField(flagDto, ['OutageStartDate', 'outageStartDate']);
        const outageEndDate = this.getField(flagDto, ['OutageEndDate', 'outageEndDate']);
        const effectiveDate = this.getField(flagDto, ['EffectiveDate', 'effectiveDate']);
        const remoteEntryEndpoint = this.getField(flagDto, ['RemoteEntryEndpoint', 'remoteEntryEndpoint']);
        const timeTravelOffsetDays = this.getField(flagDto, ['TimeTravelOffsetDays', 'timeTravelOffsetDays']);

        if (outageStartDate) {
            normalizedFlag.outageStartDate = outageStartDate;
        }
        if (outageEndDate) {
            normalizedFlag.outageEndDate = outageEndDate;
        }
        if (effectiveDate) {
            normalizedFlag.effectiveDate = effectiveDate;
        }
        if (remoteEntryEndpoint) {
            normalizedFlag.remoteEntryEndpoint = remoteEntryEndpoint;
        }
        if (timeTravelOffsetDays !== undefined && timeTravelOffsetDays !== null && timeTravelOffsetDays !== '') {
            normalizedFlag.timeTravelOffsetDays = Number(timeTravelOffsetDays);
        }

        const serverDate = this.getField(flagDto, ['ServerDate', 'serverDate']);
        if (serverDate) {
            normalizedFlag.serverDate = serverDate;
        }

        return normalizedFlag;
    }

    getField(source, keys) {
        for (let index = 0; index < keys.length; index += 1) {
            if (source && source[keys[index]] !== undefined && source[keys[index]] !== null) {
                return source[keys[index]];
            }
        }
        return undefined;
    }

    parseBoolean(value) {
        if (typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'string') {
            return value.toLowerCase() === 'true';
        }
        return Boolean(value);
    }

    getDefaultFlagByKey(fKey) {
        const defaultValue = FEATURE_FLAG_DEFAULT_VALUES[fKey];

        if (defaultValue?.config !== undefined && defaultValue?.config !== null) {
            return defaultValue.config;
        }

        return {
            isAvailable: defaultValue === 'on'
        };
    };

    getDefaultFlags() {
        return new Promise((resolve) => {
            const flagsAux = {};
            Object.keys(FEATURE_FLAG_NAMES).forEach((fKey) => {
                flagsAux[FEATURE_FLAG_NAMES[fKey]] = this.getDefaultFlagByKey(fKey);
            });
            resolve(flagsAux);
        });
    }
}